import React from 'react';
import { Library, Brain, StickyNote, MessageCircle } from 'lucide-react';
import { useI18n } from '../../i18n';

type TabType = 'library' | 'quiz' | 'notes' | 'profile';

interface MobileBottomNavProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  onAskAI?: () => void;
  isChatOpen?: boolean;
}

export function MobileBottomNav({ activeTab, onTabChange, onAskAI, isChatOpen }: MobileBottomNavProps) {
  const { t } = useI18n();

  const tabs = [
    { id: 'library' as TabType, icon: Library, label: t('nav.library') },
    { id: 'quiz' as TabType, icon: Brain, label: t('nav.challenge') },
    { id: 'notes' as TabType, icon: StickyNote, label: t('nav.notes') },
    { id: 'ask-ai' as const, icon: MessageCircle, label: t('nav.askAI') },
  ];

  return (
    <div className="fixed bottom-0 start-0 end-0 z-40 md:hidden">
      {/* Liquid glass bottom navigation */}
      <div className="bg-white/70 backdrop-blur-2xl border-t border-white/40 shadow-[0_-4px_24px_rgba(115,103,255,0.08)]">
        <div className="flex items-center justify-around px-2 py-2 relative">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isAskAI = tab.id === 'ask-ai';
            const isActive = isAskAI ? !!isChatOpen : activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => isAskAI ? onAskAI?.() : onTabChange(tab.id as TabType)}
                className={`relative flex flex-col items-center justify-center gap-1 px-4 py-2 rounded-xl transition-all duration-200 min-w-[70px] ${
                  isActive
                    ? 'bg-gradient-to-br from-[#7367FF]/10 to-[#2DD2FE]/10'
                    : 'hover:bg-white/40 active:scale-95'
                }`}
              >
                <Icon
                  className={`h-5 w-5 transition-colors ${
                    isActive ? 'text-[#7367FF]' : 'text-[#434343]'
                  }`}
                />
                <span
                  className={`text-[10px] font-medium leading-tight text-center transition-colors ${
                    isActive ? 'text-[#7367FF]' : 'text-[#434343]'
                  }`}
                >
                  {tab.label}
                </span>
                {isActive && (
                  <div className="absolute -top-1 start-1/2 -translate-x-1/2 w-8 h-1 bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] rounded-full" />
                )}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Safe area for devices with notches */}
      <div className="bg-white/70 backdrop-blur-2xl h-[env(safe-area-inset-bottom)]" />
    </div>
  );
}